<script setup>
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import ContactSortMenu from './components/ContactSortMenu.vue';
import ContactMoreActions from './components/ContactMoreActions.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';

defineProps({
  showSearch: {
    type: Boolean,
    default: true,
  },
  searchValue: {
    type: String,
    default: '',
  },
  buttonLabel: {
    type: String,
    default: '',
  },
  activeSort: {
    type: String,
    default: 'last_activity_at',
  },
  activeOrdering: {
    type: String,
    default: '',
  },
  isSegmentsView: {
    type: Boolean,
    default: false,
  },
  hasActiveFilters: {
    type: Boolean,
    default: false,
  },
  isLabelView: {
    type: Boolean,
    default: false,
  },
  showFilters: {
    type: Boolean,
    default: true,
  },
  showSortMenu: {
    type: Boolean,
    default: true,
  },
  showMoreActions: {
    type: Boolean,
    default: true,
  },
  showSendMessage: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits([
  'search',
  'filter',
  'update:sort',
  'add',
  'import',
  'export',
  'createSegment',
  'deleteSegment',
]);
</script>

<template>
  <header class="sticky top-0 z-10">
    <!-- Mobile/Tablet Layout (< xl) -->
    <div class="xl:hidden">
      <!-- Search and filter actions -->
      <div class="flex items-center gap-2 pb-3">
        <div v-if="showSearch" class="flex-1 min-w-0">
          <Input
            :model-value="searchValue"
            type="search"
            :placeholder="$t('CONTACTS_LAYOUT.HEADER.SEARCH_PLACEHOLDER')"
            :custom-input-class="[
              'h-9 ltr:!pl-3 ltr:!pr-8 rtl:!pr-3 rtl:!pl-8 !py-1 text-sm',
            ]"
            @input="emit('search', $event.target.value)"
          >
            <template #suffix>
              <Icon
                icon="i-lucide-search"
                class="absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:right-2 rtl:left-2"
              />
            </template>
          </Input>
        </div>
        <div v-if="showFilters" class="flex items-center gap-1 flex-shrink-0">
          <div v-if="!isLabelView" class="relative">
            <Button
              id="toggleContactsFilterButton"
              :icon="isSegmentsView ? 'i-lucide-pen-line' : 'i-lucide-list-filter'"
              color="slate"
              size="sm"
              class="relative w-8 h-8"
              variant="ghost"
              @click="emit('filter')"
            >
              <div
                v-if="hasActiveFilters && !isSegmentsView"
                class="absolute top-0 right-0 w-2 h-2 rounded-full bg-n-brand"
              />
            </Button>
            <slot name="filter" />
          </div>
          <Button
            v-if="hasActiveFilters && !isSegmentsView && !isLabelView"
            icon="i-lucide-save"
            color="slate"
            size="sm"
            class="w-8 h-8"
            variant="ghost"
            @click="emit('createSegment')"
          />
          <Button
            v-if="isSegmentsView && !isLabelView"
            icon="i-lucide-trash"
            color="slate"
            size="sm"
            class="w-8 h-8"
            variant="ghost"
            @click="emit('deleteSegment')"
          />
          <ContactSortMenu
            v-if="showSortMenu"
            :active-sort="activeSort"
            :active-ordering="activeOrdering"
            @update:sort="emit('update:sort', $event)"
          />
          <ContactMoreActions
            v-if="showMoreActions"
            @add="emit('add')"
            @import="emit('import')"
            @export="emit('export')"
          />
        </div>
      </div>
    </div>

    <!-- Desktop Layout (≥ xl) -->
    <div class="hidden xl:flex">
      <div class="flex items-center justify-between w-full h-10 gap-2 mx-auto">
        <div class="flex items-center flex-shrink-0 gap-2 lg:gap-4 w-full">
          <div v-if="showSearch" class="flex items-center">
            <Input
              :model-value="searchValue"
              type="search"
              :placeholder="$t('CONTACTS_LAYOUT.HEADER.SEARCH_PLACEHOLDER')"
              :custom-input-class="[
                'h-10 ltr:!pl-8 !py-1 rtl:!pr-8',
              ]"
              @input="emit('search', $event.target.value)"
            >
              <template #prefix>
                <Icon
                  icon="i-lucide-search"
                  class="absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2 rtl:right-2"
                />
              </template>
            </Input>
          </div>
          
          <!-- Chỉ hiển thị khi showFilters = true -->
          <div v-if="showFilters" class="flex items-center gap-2">
            <div v-if="!isLabelView" class="relative">
              <Button
                id="toggleContactsFilterButton"
                :icon="isSegmentsView ? 'i-lucide-pen-line' : 'i-lucide-list-filter'"
                color="slate"
                size="sm"
                class="relative w-8"
                variant="ghost"
                @click="emit('filter')"
              >
                <div
                  v-if="hasActiveFilters && !isSegmentsView"
                  class="absolute top-0 right-0 w-2 h-2 rounded-full bg-n-brand"
                />
              </Button>
              <slot name="filter" />
            </div>
            <Button
              v-if="hasActiveFilters && !isSegmentsView && !isLabelView"
              icon="i-lucide-save"
              color="slate"
              size="sm"
              variant="ghost"
              @click="emit('createSegment')"
            />
            <Button
              v-if="isSegmentsView && !isLabelView"
              icon="i-lucide-trash"
              color="slate"
              size="sm"
              variant="ghost"
              @click="emit('deleteSegment')"
            />
            
            <ContactSortMenu
              v-if="showSortMenu"
              :active-sort="activeSort"
              :active-ordering="activeOrdering"
              @update:sort="emit('update:sort', $event)"
            />
            
            <ContactMoreActions
              v-if="showMoreActions"
              @add="emit('add')"
              @import="emit('import')"
              @export="emit('export')"
            />
          </div>
          
          <div v-if="showFilters && showSendMessage" class="w-px h-4 bg-n-strong" />
          
          <ComposeConversation v-if="showSendMessage">
            <template #trigger="{ toggle }">
              <Button
                class="!bg-grass-400 font-medium !h-10 p-[10px]"
                :label="buttonLabel"
                size="sm"
                trailing-icon
                icon="i-lucide-send"
                @click="toggle"
              />
            </template>
          </ComposeConversation>
        </div>
      </div>
    </div>
  </header>
</template>
